import React, { useEffect, useState } from "react";

import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Collapse,
    TextField,
    Typography,
} from "@mui/material";

import { enumAuthTypes } from "enums/lead/auth-types";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";

import {
    getDestinationFacebook,
    googleEnhanceAuth,
} from "helpers/destinations/createDestination";
import { getGoogleDmaAuthUrl } from "helpers/destinations/googleDma";
import { reconnectDestination } from "helpers/destinations/reconnectDestination";
import { getYandexMetricaAuthUrl } from "helpers/destinations/yandexMetrica";

import CreateTwitterDestinationPopup from "components/CreateTwitterDestinationPopup";
import FaceBookSdk from "components/Leads/SharedComponents/FaceBookSdk";
import LoginButton from "components/molecules/LoginButton";
import ReconnectReddit from "components/organisms/SDKs/RedditCapi/ReconnectReddit";
import PinterestAuthButton from "components/PinterestAuthButton";
import ReconnectTikTokDestination from "components/ReconnectTiktokDestination";
import SwalAlertComp from "components/SwalAlert";

import { ReconnectTabProps } from "../../../DestinationModal.types";
import { useDestinationReconnect } from "../../../hooks/useDestinationReconnect";

const ReconnectTab: React.FC<ReconnectTabProps> = ({
    destination,
    language,
    integrationLogs,
    isLoadingLogs,
    onRefresh,
}) => {
    const destinationCode = destination?.destination_type?.code;
    const appObject = useSelector((state: any) => state.setAppId.value);
    const bussiness = useSelector(
        (state: any) =>
            state.allDashboardDatasState?.getAllDashboardDatas?.bussiness || [],
    );

    const {
        formData,
        errorMessages,
        isReconnectLoading,
        buttonStatus,
        authData,
        slug,
        beExtra,
        setAuthData,
        setSlug,
        setBeExtra,
        setFormData,
        handleFormChange,
        handleReconnect,
        reconnectButtonLabel,
        setIsReconnectLoading,
    } = useDestinationReconnect({
        destination,
        integrationLogs,
        isLoadingLogs,
        onReconnectSuccess: onRefresh,
        language,
    });

    const [openAiKeys, setOpenAiKeys] = useState({
        conversions_api_key: "",
        advertiser_api_key: "",
    });
    const [openAiKeyErrors, setOpenAiKeyErrors] = useState<{
        conversions_api_key?: string;
        advertiser_api_key?: string;
    }>({});

    const handleOpenAiReconnect = async () => {
        const blankMessage =
            language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!";
        const conversionsApiKey = openAiKeys.conversions_api_key.trim();
        const advertiserApiKey = openAiKeys.advertiser_api_key.trim();
        const errors: {
            conversions_api_key?: string;
            advertiser_api_key?: string;
        } = {};

        if (!conversionsApiKey) errors.conversions_api_key = blankMessage;
        if (!advertiserApiKey) errors.advertiser_api_key = blankMessage;

        setOpenAiKeyErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setIsReconnectLoading(true);
        try {
            const response = await reconnectDestination(destination.id, {
                advertiser_api_key: advertiserApiKey,
                conversions_api_key: conversionsApiKey,
            });

            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message,
                    icon: "success",
                    timer: 3000,
                });
                setOpenAiKeys({
                    conversions_api_key: "",
                    advertiser_api_key: "",
                });
                onRefresh?.();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
        } finally {
            setIsReconnectLoading(false);
        }
    };

    useEffect(() => {
        const fetchFacebookData = async () => {
            if (destinationCode === "fbcapi" || destinationCode === "fbpixel") {
                try {
                    const response: any = await getDestinationFacebook();
                    setSlug(response?.data?.external_business_id || "");
                } catch (error) {
                    console.error("Error fetching Facebook data:", error);
                }
            }
        };

        fetchFacebookData();
    }, [destinationCode, setSlug]);

    useEffect(() => {
        if (
            slug &&
            slug.length > 0 &&
            (destinationCode === "fbcapi" || destinationCode === "fbpixel")
        ) {
            const businessIdObject = bussiness.find(
                (business: any) => business.selected === true,
            );

            if (businessIdObject) {
                setBeExtra({
                    setup: {
                        external_business_id: slug,
                        timezone: "UTC",
                        currency: businessIdObject.currency,
                        channel: "SIGNALS",
                        business_vertical: businessIdObject.vertical,
                        pixel_id: destination?.pixel_id,
                    },
                    business_config: {
                        business: {
                            name: businessIdObject.name,
                        },
                    },
                    repeat: false,
                });
            }
        }
    }, [
        slug,
        destination?.pixel_id,
        bussiness,
        appObject,
        destinationCode,
        setBeExtra,
    ]);

    const fbReconnectUpdatedData = async (accessToken: string) => {
        if (!accessToken) return;
        setIsReconnectLoading(true);
        const fb = (window as any).FB;
        if (!fb?.api) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    (language?.delete_swal_modal as any)
                        ?.error_pixel_found_user_authorize ??
                    "Error. No Pixel found! User cancelled login or did not fully authorize.",
                icon: "error",
            });
            setIsReconnectLoading(false);
            return;
        }
        fb.api(
            "fbe_business/fbe_installs?fbe_external_business_id=" + slug,
            async (response: any) => {
                setIsReconnectLoading(true);
                if (response?.data && response.data.length > 0) {
                    const pixelArray = response.data.map(
                        (item: any) => item.pixel_id,
                    );
                    const pixelArrayFirstEl = response.data[0];
                    const form = {
                        access_token: accessToken,
                        pixels: pixelArray,
                        extra_data: pixelArrayFirstEl,
                        external_business_id: slug,
                        type: enumAuthTypes.BUTTON,
                    };
                    const result = await reconnectDestination(
                        destination.id,
                        form,
                    );
                    if (result.status) {
                        onRefresh();
                        SwalAlertComp({
                            title:
                                language?.delete_swal_modal?.success ??
                                "Success",
                            message: result.message,
                            icon: "success",
                            timer: 3000,
                        });
                        setFormData({ ...formData, token: "" });
                    } else {
                        SwalAlertComp({
                            title:
                                language?.delete_swal_modal?.failed ?? "Failed",
                            message: result.message,
                            icon: "error",
                        });
                    }
                } else {
                    SwalAlertComp({
                        title: language?.delete_swal_modal?.failed ?? "Failed",
                        message:
                            (language?.delete_swal_modal as any)
                                ?.error_pixel_found_user_authorize ??
                            "Error. No Pixel found! User cancelled login or did not fully authorize.",
                        icon: "error",
                    });
                }
                setIsReconnectLoading(false);
            },
        );
    };

    const reconnectTtByData = async (data: {
        code: string;
        state?: string;
    }) => {
        setIsReconnectLoading(true);
        const response = await reconnectDestination(destination.id, {
            code: data.code,
            state: data.state,
            type: enumAuthTypes.BUTTON,
        });
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            setFormData({ ...formData, token: "" });
            onRefresh();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message ?? "",
                icon: "error",
            });
        }
        setIsReconnectLoading(false);
    };

    const reconnectPnByData = async (data: {
        code: string;
        state?: string;
    }) => {
        setIsReconnectLoading(true);
        const response = await reconnectDestination(destination.id, {
            code: data.code,
            state: data.state,
            type: enumAuthTypes.BUTTON,
        });
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            setFormData({ ...formData, token: "", ad_account_id: "" });
            onRefresh();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message ?? "",
                icon: "error",
            });
        }
        setIsReconnectLoading(false);
    };

    const reconnectYandexByData = async (data: {
        code: string;
        state: string;
    }) => {
        setIsReconnectLoading(true);
        const response = await reconnectDestination(destination.id, {
            code: data.code,
            state: data.state,
        });
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            onRefresh();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message ?? "",
                icon: "error",
            });
        }
        setIsReconnectLoading(false);
    };

    const reconnectGgByData = async (data: { code: string }) => {
        setIsReconnectLoading(true);
        const response = await reconnectDestination(destination.id, data);
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            setFormData({ pixel_id: "", token: "" });
            onRefresh();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message ?? "",
                icon: "error",
            });
        }
        setIsReconnectLoading(false);
    };

    const sendDataToGoogleReconnect = (data: any) => {
        if (data?.google_code) {
            reconnectGgByData({ code: data.google_code });
        }
    };

    const reconnectTwByData = async (data: any) => {
        setIsReconnectLoading(true);
        const response = await reconnectDestination(destination.id, data);
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            setFormData({ pixel_id: "", token: "" });
            onRefresh();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message ?? "",
                icon: "error",
            });
        }
        setIsReconnectLoading(false);
    };

    const reconnectRdByData = async (data: { code: string }) => {
        setIsReconnectLoading(true);
        const response = await reconnectDestination(destination.id, {
            code: data.code,
            type: enumAuthTypes.BUTTON,
        });
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            setFormData({ ...formData, token: "" });
            onRefresh();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message ?? "",
                icon: "error",
            });
        }
        setIsReconnectLoading(false);
    };

    useEffect(() => {
        if (
            (destinationCode !== "ttcapi" && destinationCode !== "ttpixel") ||
            authData?.code == null
        ) {
            return;
        }
        const data = authData;
        setAuthData(null);
        reconnectTtByData(data);
    }, [authData, destinationCode]);

    useEffect(() => {
        if (destinationCode !== "pncapi" || authData?.code == null) return;
        const data = authData;
        setAuthData(null);
        reconnectPnByData(data);
    }, [authData, destinationCode]);

    useEffect(() => {
        if (
            (destinationCode !== "twpixel" && destinationCode !== "twcapi") ||
            !authData?.oauth
        ) {
            return;
        }
        const data = authData;
        setAuthData(null);
        reconnectTwByData(data);
    }, [authData, destinationCode]);

    useEffect(() => {
        if (
            (destinationCode !== "rdcapi" && destinationCode !== "rdpixel") ||
            !authData?.code
        ) {
            return;
        }
        const data = authData;
        setAuthData(null);
        reconnectRdByData(data);
    }, [authData, destinationCode]);

    const renderReconnectContent = () => {
        if (destinationCode === "fbcapi" || destinationCode === "fbpixel") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <div>
                                    <div className="position-relative overflow-hidden z-1">
                                        {isReconnectLoading && (
                                            <div className="circle-loader">
                                                <ClipLoader
                                                    color={"#000"}
                                                    size={17}
                                                />
                                            </div>
                                        )}
                                        <FaceBookSdk
                                            {...({
                                                appId:
                                                    appObject.app_id &&
                                                    appObject.app_id.toString(),
                                                updatedData:
                                                    fbReconnectUpdatedData,
                                                scope: "email,manage_business_extension,ads_management,pages_read_engagement,ads_read,business_management",
                                                text: "Continue with Facebook CAPI",
                                                extras: beExtra,
                                                version:
                                                    appObject.version &&
                                                    appObject.version.toString(),
                                                setModalLoading:
                                                    setIsReconnectLoading,
                                            } as any)}
                                        />
                                    </div>
                                    {buttonStatus === false && (
                                        <>
                                            <hr
                                                className="divider-hr"
                                                data-text={
                                                    language?.tracker_detail
                                                        ?.or ?? "or"
                                                }
                                            />
                                            <Form.Label>Pixel ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="pixel_id"
                                                placeholder="Pixel ID"
                                                onChange={handleFormChange}
                                                value={formData?.pixel_id}
                                                disabled
                                            />
                                            <Form.Label>Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="token"
                                                placeholder="Token"
                                                onChange={handleFormChange}
                                                disabled={isReconnectLoading}
                                                style={
                                                    errorMessages?.token
                                                        ? { borderColor: "red" }
                                                        : {}
                                                }
                                                value={formData?.token}
                                                required
                                            />
                                            {errorMessages?.token && (
                                                <div className="fileUploadStyle">
                                                    {language?.tracker_wizard
                                                        ?.cannot_be_blank ??
                                                        "Cannot be blank!"}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    <Modal.Footer>
                        {reconnectButtonLabel()}
                        <div className="relative">
                            {isReconnectLoading && (
                                <div className="circle-loader">
                                    <ClipLoader color={"#000"} size={17} />
                                </div>
                            )}
                            {buttonStatus === false && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleReconnect()}
                                >
                                    {language?.buttons?.reconnect ??
                                        "Reconnect"}
                                </Button>
                            )}
                        </div>
                    </Modal.Footer>
                </>
            );
        }

        if (destinationCode === "ttcapi" || destinationCode === "ttpixel") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <div>
                                    <div className="position-relative overflow-hidden z-1">
                                        <ReconnectTikTokDestination
                                            setAuthData={setAuthData}
                                            isLoading={isReconnectLoading}
                                        />
                                    </div>
                                    <hr
                                        className="divider-hr"
                                        data-text={
                                            language?.tracker_detail?.or ?? "or"
                                        }
                                    />
                                    {buttonStatus === false && (
                                        <>
                                            <Form.Label>Pixel ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="pixel_id"
                                                placeholder="Pixel ID"
                                                onChange={handleFormChange}
                                                value={formData?.pixel_id}
                                                disabled
                                            />
                                            <Form.Label>Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="token"
                                                placeholder="Token"
                                                onChange={handleFormChange}
                                                style={
                                                    errorMessages?.token
                                                        ? { borderColor: "red" }
                                                        : {}
                                                }
                                                value={formData?.token}
                                                required
                                            />
                                            {errorMessages?.token && (
                                                <div className="fileUploadStyle">
                                                    {language?.tracker_wizard
                                                        ?.cannot_be_blank ??
                                                        "Cannot be blank!"}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    <Modal.Footer>
                        {reconnectButtonLabel()}
                        {buttonStatus === false && (
                            <div className="relative">
                                {isReconnectLoading && (
                                    <div className="circle-loader">
                                        <ClipLoader color={"#000"} size={17} />
                                    </div>
                                )}
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleReconnect()}
                                    disabled={isReconnectLoading}
                                >
                                    {language?.buttons?.reconnect ??
                                        "Reconnect"}
                                </Button>
                            </div>
                        )}
                    </Modal.Footer>
                </>
            );
        }

        if (destinationCode === "rdcapi" || destinationCode === "rdpixel") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <div>
                                    <div className="position-relative overflow-hidden z-1">
                                        <ReconnectReddit
                                            label="continue with reddit capi"
                                            setAuthData={setAuthData}
                                            isLoading={isReconnectLoading}
                                        />
                                    </div>
                                    <hr
                                        className="divider-hr"
                                        data-text={
                                            language?.tracker_detail?.or ?? "or"
                                        }
                                    />
                                    {buttonStatus === false && (
                                        <>
                                            <Form.Label>Pixel ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="pixel_id"
                                                placeholder="Pixel ID"
                                                onChange={handleFormChange}
                                                value={formData?.pixel_id}
                                                disabled
                                            />
                                            <Form.Label>Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="token"
                                                placeholder="Token"
                                                onChange={handleFormChange}
                                                style={
                                                    errorMessages?.token
                                                        ? { borderColor: "red" }
                                                        : {}
                                                }
                                                value={formData?.token}
                                                required
                                            />
                                            {errorMessages?.token && (
                                                <div className="fileUploadStyle">
                                                    {language?.tracker_wizard
                                                        ?.cannot_be_blank ??
                                                        "Cannot be blank!"}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    <Modal.Footer>
                        {reconnectButtonLabel()}
                        {buttonStatus === false && (
                            <div className="relative">
                                {isReconnectLoading && (
                                    <div className="circle-loader">
                                        <ClipLoader color={"#000"} size={17} />
                                    </div>
                                )}
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleReconnect()}
                                >
                                    {language?.buttons?.reconnect ??
                                        "Reconnect"}
                                </Button>
                            </div>
                        )}
                    </Modal.Footer>
                </>
            );
        }

        if (destinationCode === "scpixel" || destinationCode === "sccapi") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <div>
                                    <Form.Label>Pixel ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        className="mb-2"
                                        name="pixel_id"
                                        placeholder="Pixel ID"
                                        onChange={handleFormChange}
                                        value={formData?.pixel_id}
                                        disabled
                                    />
                                    <Form.Label>Token</Form.Label>
                                    <Form.Control
                                        type="text"
                                        className="mb-2"
                                        name="token"
                                        placeholder="Token"
                                        onChange={handleFormChange}
                                        style={
                                            errorMessages?.token
                                                ? { borderColor: "red" }
                                                : {}
                                        }
                                        value={formData?.token}
                                        required
                                    />
                                    {errorMessages?.token && (
                                        <div className="fileUploadStyle">
                                            {language?.tracker_wizard
                                                ?.cannot_be_blank ??
                                                "Cannot be blank!"}
                                        </div>
                                    )}
                                </div>
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    <Modal.Footer>
                        {reconnectButtonLabel()}
                        <div className="relative">
                            {isReconnectLoading && (
                                <div className="circle-loader">
                                    <ClipLoader color={"#000"} size={17} />
                                </div>
                            )}
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={() => handleReconnect()}
                            >
                                {language?.buttons?.reconnect ?? "Reconnect"}
                            </Button>
                        </div>
                    </Modal.Footer>
                </>
            );
        }

        if (destinationCode === "pncapi") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <div className="position-relative overflow-hidden z-1">
                                    <PinterestAuthButton
                                        setAuthData={setAuthData}
                                    />
                                    <hr
                                        className="divider-hr"
                                        data-text={
                                            language?.tracker_detail?.or ?? "or"
                                        }
                                    />
                                    {buttonStatus === false && (
                                        <>
                                            <Form.Label>Tag ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="pixel_id"
                                                placeholder="Tag ID"
                                                onChange={handleFormChange}
                                                value={formData?.pixel_id}
                                                disabled
                                            />
                                            <Form.Label>
                                                Access Token
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="token"
                                                placeholder="Access Token"
                                                onChange={handleFormChange}
                                                style={
                                                    errorMessages?.token
                                                        ? { borderColor: "red" }
                                                        : {}
                                                }
                                                value={formData?.token}
                                                required
                                            />
                                            {errorMessages?.token && (
                                                <div className="fileUploadStyle">
                                                    {language?.tracker_wizard
                                                        ?.cannot_be_blank ??
                                                        "Cannot be blank!"}
                                                </div>
                                            )}
                                            <Form.Label>
                                                Ad Account ID
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="mb-2"
                                                name="ad_account_id"
                                                placeholder="Ad Account ID"
                                                onChange={handleFormChange}
                                                style={
                                                    errorMessages?.ad_account_id
                                                        ? { borderColor: "red" }
                                                        : {}
                                                }
                                                value={formData?.ad_account_id}
                                                required
                                            />
                                            {errorMessages?.ad_account_id && (
                                                <div className="fileUploadStyle">
                                                    {language?.tracker_wizard
                                                        ?.cannot_be_blank ??
                                                        "Cannot be blank!"}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    <Modal.Footer>
                        {reconnectButtonLabel()}
                        {buttonStatus === false && (
                            <div className="relative">
                                {isReconnectLoading && (
                                    <div className="circle-loader">
                                        <ClipLoader color={"#000"} size={17} />
                                    </div>
                                )}
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleReconnect()}
                                >
                                    {language?.buttons?.reconnect ??
                                        "Reconnect"}
                                </Button>
                            </div>
                        )}
                    </Modal.Footer>
                </>
            );
        }

        if (destinationCode === "ggalc" || destinationCode === "ggass") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <LoginButton
                                    code="google"
                                    getAuthUrl={() =>
                                        getGoogleDmaAuthUrl(destinationCode)
                                    }
                                    getAuthUrlResponseTransform={(response) =>
                                        response?.data?.url
                                    }
                                    paramNames={["code"]}
                                    onSuccess={reconnectGgByData}
                                    dataTransform={(params) => ({
                                        code: params.code,
                                    })}
                                    logoSrc="/assets/images/google.png"
                                    buttonText="Connect with Google"
                                    isLoadingExternal={isReconnectLoading}
                                />
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    {reconnectButtonLabel()}
                </>
            );
        }

        if (destinationCode === "ggads" || destinationCode === "ggads_web") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <div>
                                    <div>
                                        <LoginButton
                                            code="google"
                                            getAuthUrl={googleEnhanceAuth}
                                            getAuthUrlData={{
                                                type_name: "google_adwords",
                                            }}
                                            getAuthUrlResponseTransform={(
                                                response,
                                            ) => {
                                                return response?.data?.url;
                                            }}
                                            paramNames={["code"]}
                                            onSuccess={
                                                sendDataToGoogleReconnect
                                            }
                                            dataTransform={(params) => ({
                                                google_code: params.code,
                                            })}
                                            logoSrc="/assets/images/google.png"
                                            buttonText={"Connect with Google"}
                                            isLoadingExternal={
                                                isReconnectLoading
                                            }
                                        />
                                    </div>
                                </div>
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    {reconnectButtonLabel()}
                </>
            );
        }

        if (destinationCode === "twpixel" || destinationCode === "twcapi") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <CreateTwitterDestinationPopup
                                    setIsOauthData={setAuthData}
                                    type={destinationCode}
                                    isLoading={isReconnectLoading}
                                />
                                <hr
                                    className="divider-hr"
                                    data-text={
                                        language?.tracker_detail?.or ?? "or"
                                    }
                                />
                                {buttonStatus === false && (
                                    <>
                                        <Form.Label>Pixel ID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="mb-2"
                                            name="pixel_id"
                                            placeholder="Pixel ID"
                                            onChange={handleFormChange}
                                            value={formData?.pixel_id}
                                            disabled
                                        />
                                        <Form.Label>Token</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="mb-2"
                                            name="token"
                                            placeholder="Token"
                                            onChange={handleFormChange}
                                            style={
                                                errorMessages?.token
                                                    ? { borderColor: "red" }
                                                    : {}
                                            }
                                            value={formData?.token}
                                            required
                                        />
                                        {errorMessages?.token && (
                                            <div className="fileUploadStyle">
                                                {language?.tracker_wizard
                                                    ?.cannot_be_blank ??
                                                    "Cannot be blank!"}
                                            </div>
                                        )}
                                    </>
                                )}
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    <Modal.Footer>
                        {reconnectButtonLabel()}
                        {buttonStatus === false && (
                            <div className="relative">
                                {isReconnectLoading && (
                                    <div className="circle-loader">
                                        <ClipLoader color={"#000"} size={17} />
                                    </div>
                                )}
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleReconnect()}
                                >
                                    {language?.buttons?.reconnect ??
                                        "Reconnect"}
                                </Button>
                            </div>
                        )}
                    </Modal.Footer>
                </>
            );
        }

        if (destinationCode === "alpixel") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                {buttonStatus === false && (
                                    <>
                                        <Form.Label>Pixel ID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="mb-2"
                                            name="pixel_id"
                                            placeholder="Pixel ID"
                                            onChange={handleFormChange}
                                            value={formData?.pixel_id}
                                            disabled
                                        />
                                    </>
                                )}
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    <Modal.Footer>
                        {reconnectButtonLabel()}
                        {buttonStatus === false && (
                            <div className="relative">
                                {isReconnectLoading && (
                                    <div className="circle-loader">
                                        <ClipLoader color={"#000"} size={17} />
                                    </div>
                                )}
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleReconnect()}
                                >
                                    {language?.buttons?.reconnect ??
                                        "Reconnect"}
                                </Button>
                            </div>
                        )}
                    </Modal.Footer>
                </>
            );
        }

        if (destinationCode === "ymwc") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <LoginButton
                                    code="ymwc"
                                    getAuthUrl={() =>
                                        getYandexMetricaAuthUrl().then(
                                            (response) =>
                                                response?.status &&
                                                response?.data?.url
                                                    ? response.data.url
                                                    : null,
                                        )
                                    }
                                    paramNames={["code", "state"]}
                                    requireAllParams
                                    onSuccess={reconnectYandexByData}
                                    dataTransform={(params) => ({
                                        code: params.code,
                                        state: params.state,
                                    })}
                                    logoSrc="/assets/images/yandex.png"
                                    buttonText="Connect Yandex Metrica"
                                    isLoadingExternal={isReconnectLoading}
                                />
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    {reconnectButtonLabel()}
                </>
            );
        }

        if (destinationCode === "oaicapi") {
            return (
                <>
                    <Form>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <TextField
                                    className="mb-3"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    name="conversions_api_key"
                                    label="Conversions Key"
                                    placeholder="Conversions Key"
                                    slotProps={{
                                        input: { inputComponent: "textarea" },
                                    }}
                                    value={openAiKeys.conversions_api_key}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setOpenAiKeys((prev) => ({
                                            ...prev,
                                            conversions_api_key: value,
                                        }));
                                        setOpenAiKeyErrors((prev) => ({
                                            ...prev,
                                            conversions_api_key: value
                                                ? ""
                                                : (language?.tracker_wizard
                                                      ?.cannot_be_blank ??
                                                  "Cannot be blank!"),
                                        }));
                                    }}
                                    error={
                                        !!openAiKeyErrors.conversions_api_key
                                    }
                                    helperText={
                                        openAiKeyErrors.conversions_api_key ||
                                        ""
                                    }
                                    required
                                />
                                <TextField
                                    className="mb-2"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    name="advertiser_api_key"
                                    label="Ads Management Key"
                                    placeholder="Ads Management Key"
                                    slotProps={{
                                        input: { inputComponent: "textarea" },
                                    }}
                                    value={openAiKeys.advertiser_api_key}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setOpenAiKeys((prev) => ({
                                            ...prev,
                                            advertiser_api_key: value,
                                        }));
                                        setOpenAiKeyErrors((prev) => ({
                                            ...prev,
                                            advertiser_api_key: value
                                                ? ""
                                                : (language?.tracker_wizard
                                                      ?.cannot_be_blank ??
                                                  "Cannot be blank!"),
                                        }));
                                    }}
                                    error={!!openAiKeyErrors.advertiser_api_key}
                                    helperText={
                                        openAiKeyErrors.advertiser_api_key || ""
                                    }
                                    required
                                />
                            </Form.Group>
                        </Modal.Body>
                    </Form>
                    <Modal.Footer>
                        <div className="relative">
                            {isReconnectLoading && (
                                <div className="circle-loader">
                                    <ClipLoader color={"#000"} size={17} />
                                </div>
                            )}
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleOpenAiReconnect}
                                disabled={isReconnectLoading}
                            >
                                {language?.buttons?.reconnect ?? "Reconnect"}
                            </Button>
                        </div>
                    </Modal.Footer>
                </>
            );
        }

        return null;
    };

    const isDisconnected = destination?.status === 0;

    return (
        <div>
            <Collapse in={isDisconnected}>
                <Alert severity="warning" className="mb-3 border-radius-2">
                    <AlertTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
                        Connection Lost
                    </AlertTitle>
                    Since the Access Token is expired, you must reconnect the
                    destination to resume data synchronization.
                </Alert>
            </Collapse>

            <Typography variant="h5" fontWeight="bold" gutterBottom>
                {language?.buttons?.reconnect ?? "Reconnect"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Re-establishes the connection with the destination to ensure
                ongoing data transfer and resolve any connectivity issues.
            </Typography>

            <div className="edit-mapping-area">{renderReconnectContent()}</div>
        </div>
    );
};

export default ReconnectTab;
